var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { Input, Option, Select } from "@mui/joy";
import moment from "moment";
import { replaceMultiple } from "@/utils/funcs";
import { useAppSelector } from "@/hooks/store";
import { getCurrentUserDateFormat, getCurrentUserTimeFormat, } from "@/store/utils/selectors/userSelector";
/** <b>Composant d'affichage en mode édition d'un champ de type enum</b>*/
export default function EnumFieldEdit(_a) {
    var fieldName = _a.fieldName, record = _a.record, onUpdate = _a.onUpdate, fieldMetadata = _a.fieldMetadata, isOnStudio = _a.isOnStudio, moduleName = _a.moduleName, isReport = _a.isReport;
    var t = useTranslation(["common"]).t;
    /*
    const { data: queryResult } = useGetCurrentUserQuery(null);
    const user_preferences = queryResult?.data?.preferences?.date;
    const dateFormat = user_preferences?.date_format;
    const timeFormat = user_preferences?.time_format;
    */
    var dateFormat = useAppSelector(getCurrentUserDateFormat);
    var timeFormat = useAppSelector(getCurrentUserTimeFormat);
    var replacements = {
        m: "MM",
        d: "DD",
        Y: "yyyy",
        H: "HH",
        i: "mm",
        a: "a",
    };
    var dateTimeFormat = useMemo(function () { return replaceMultiple("".concat(dateFormat, " ").concat(timeFormat), replacements); }, [dateFormat, timeFormat]);
    var newFormatString = useMemo(function () {
        return dateTimeFormat
            .replace("m", "MM")
            .replace("Y", "yyyy")
            .replace("d", "DD")
            .replace("H", "HH")
            .replace("i", "mm");
    }, [dateTimeFormat]);
    var options = function () {
        var optionsObejct = t("app_lists")[fieldMetadata.options] || {};
        var values = [];
        for (var p in optionsObejct) {
            values.push({ value: p, label: optionsObejct[p] });
        }
        return values;
    };
    var optionList = options();
    var getLabelFromValue = function (value) {
        var option = optionList && (optionList === null || optionList === void 0 ? void 0 : optionList.find(function (opt) { return opt.value == value; }));
        return option ? option.label : undefined;
    };
    var getValueFromLabel = function (label) {
        var option = optionList.find(function (opt) { return opt.label == label; });
        return option ? option.value : "";
    };
    var durationText = function (seconds) {
        var units = [
            {
                labelSingular: t("app.LBL_DURATION_DAY", { ns: "common" }),
                labelPlural: t("app.LBL_DURATION_DAYS", { ns: "common" }),
                seconds: 24 * 3600,
            },
            {
                labelSingular: t("app.LBL_DURATION_HOUR", { ns: "common" }),
                labelPlural: t("app.LBL_DURATION_HOURS", { ns: "common" }),
                seconds: 3600,
            },
            {
                labelSingular: t("app.LBL_DURATION_MINUTE", { ns: "common" }),
                labelPlural: t("app.LBL_DURATION_MINUTES", { ns: "common" }),
                seconds: 60,
            },
        ];
        var remainingSeconds = seconds;
        var result = "";
        units.forEach(function (unit) {
            if (remainingSeconds >= unit.seconds) {
                var value_1 = Math.floor(remainingSeconds / unit.seconds);
                remainingSeconds %= unit.seconds;
                result += "".concat(value_1, " ").concat(value_1 > 1 ? unit.labelPlural : unit.labelSingular, " ");
            }
        });
        return result.trim();
    };
    var defaultValue = useMemo(function () {
        if (!record) {
            return;
        }
        else {
            if (isReport) {
                return record.value;
            }
            else if (fieldName === "duration" && moduleName === "Meetings") {
                var duration_hours = record["duration_hours"];
                var duration_minutes = record["duration_minutes"];
                var duration_1 = record[fieldName];
                if (record["duration"] === "") {
                    var heuresEnSeconde = duration_hours * 3600;
                    var minutesEnSeconde = duration_minutes * 60;
                    var v = heuresEnSeconde + minutesEnSeconde;
                    return v.toString();
                }
                else if (duration_1 !== "") {
                    return duration_1;
                }
                else {
                    return record[fieldName] || (fieldMetadata === null || fieldMetadata === void 0 ? void 0 : fieldMetadata.default);
                }
            }
            else {
                return record[fieldName] || (fieldMetadata === null || fieldMetadata === void 0 ? void 0 : fieldMetadata.default);
            }
        }
    }, [fieldName, record, moduleName]);
    var _b = useState(defaultValue || ""), value = _b[0], setValue = _b[1];
    useEffect(function () {
        if (defaultValue !== undefined)
            setValue(defaultValue);
    }, [defaultValue]);
    var _c = useState(0), key = _c[0], setKey = _c[1]; // State to hold the key value
    useEffect(function () {
        // Update the key value whenever defaultValue changes
        setKey(function (prevKey) { return prevKey + 1; });
    }, [defaultValue]);
    var onChangeSelect = function (event, newValue) {
        var _a;
        var duration_hours = Math.floor(newValue / 3600);
        var duration_minutes = Math.floor((newValue % 3600) / 60);
        setValue(newValue || "");
        if (onUpdate) {
            if (fieldName === "duration" && moduleName === "Meetings") {
                record["duration_hours"] = duration_hours;
                record["duration_minutes"] = duration_minutes;
                var originalDate = record["date_start"];
                var date = moment(originalDate, dateTimeFormat).add(newValue, "seconds");
                var dateEnd = date.format(dateTimeFormat);
                record["date_end"] = dateEnd;
            }
            onUpdate((_a = {},
                _a[fieldName] = newValue,
                _a.record = record,
                _a));
        }
    };
    var isValueInOptions = optionList.some(function (option) { return option.value === value; });
    return (_jsx("div", { children: isOnStudio ? (_jsx(Input, { type: "text", className: "w-full" })) : (_jsx(_Fragment, { children: _jsxs(Select, __assign({ className: "w-full h-8 rounded-md border border-[--color-gray-300] p-1 justify-center shadow cursor-pointer", value: value, onChange: function (event, newValue) {
                    onChangeSelect(event, newValue);
                } }, { children: [optionList &&
                        optionList.map(function (option, index) { return (_jsx(_Fragment, { children: _jsx(Option, __assign({ value: option.value }, { children: option.label }), index) })); }), !isValueInOptions && (_jsx(Option, __assign({ value: value }, { children: durationText(Number(value)) })))] }), key) })) }));
}
